import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
  Table
} from 'react-bootstrap';
import {
  FaUserPlus,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt,
  FaEnvelope,
  FaCreditCard,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaEdit,
  FaKey
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

interface UserSetupData {
  // Basic user info
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone?: string;
  user_type: 'agent' | 'broker' | 'admin' | 'internal';
  
  // Subscription setup (REQUIRED)
  subscription_package: string;
  subscription_role: string;
  
  // Access control
  screen_access_type: 'default' | 'custom';
  custom_screens?: string[];
  
  // Agent-specific
  brokerage_id?: string;
  agent_license?: string;
  
  // Invitation
  send_invitation: boolean;
}

interface ExistingUser {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_type?: string;
  user_status?: string;
  created_on?: string;
  has_subscription: boolean;
  has_screen_access: boolean;
  subscription_package?: string;
  subscription_status?: string;
  brokerage_name?: string;
  issues: string[];
  has_issues: boolean;
}

interface SubscriptionPackage {
  id: number;
  subscription_package_name: string;
  monthly_price: number;
  is_active: boolean;
}

interface UserRole {
  id: string;
  role_name: string;
  role_description: string;
  is_active: boolean;
}

const CompleteUserSetup: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'create' | 'fix'>('create');
  const [formData, setFormData] = useState<UserSetupData>({
    user_first_name: '',
    user_last_name: '',
    user_email: '',
    user_type: 'agent',
    subscription_package: '',
    subscription_role: 'Member',
    screen_access_type: 'default',
    send_invitation: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [brokerages, setBrokerages] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<ExistingUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ExistingUser | null>(null);
  const [showFixModal, setShowFixModal] = useState(false);
  const [showUpdateRoleModal, setShowUpdateRoleModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [roleUpdateData, setRoleUpdateData] = useState({
    role_id: '',
    subscription_package: '',
    subscription_role: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch all required data in parallel
      const [packagesRes, brokeragesRes, rolesRes] = await Promise.all([
        axiosInstance.get('/admin/subscription-packages'),
        axiosInstance.get('/admin/brokerages'),
        axiosInstance.get('/admin/user-roles')
      ]);

      setPackages(packagesRes.data.subscription_packages || []);
      setBrokerages(brokeragesRes.data.brokerages || []);
      setUserRoles(rolesRes.data.roles || []);
      
      console.log('Loaded roles:', rolesRes.data.roles);
      console.log('Loaded packages:', packagesRes.data.subscription_packages);

      // Set default package based on user type
      if (packagesRes.data.subscription_packages?.length > 0) {
        const defaultPackage = packagesRes.data.subscription_packages.find(
          (p: SubscriptionPackage) => p.subscription_package_name === 'ProAgentMonthly'
        ) || packagesRes.data.subscription_packages[0];
        setFormData(prev => ({ ...prev, subscription_package: defaultPackage.subscription_package_name }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load setup data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/users/all');
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.user_first_name) errors.user_first_name = 'First name is required';
    if (!formData.user_last_name) errors.user_last_name = 'Last name is required';
    if (!formData.user_email) errors.user_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user_email)) {
      errors.user_email = 'Invalid email format';
    }
    if (!formData.subscription_package) errors.subscription_package = 'Subscription package is required';
    if (formData.user_type === 'agent' && !formData.brokerage_id) {
      errors.brokerage_id = 'Brokerage is required for agents';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSaving(true);
    try {
      // Call the new complete user setup endpoint
      const response = await axiosInstance.post('/admin/users/complete-setup', formData);
      
      if (response.data.success) {
        toast.success('User created successfully with all required configurations!');
        
        // Reset form
        setFormData({
          user_first_name: '',
          user_last_name: '',
          user_email: '',
          user_type: 'agent',
          subscription_package: packages[0]?.subscription_package_name || '',
          subscription_role: 'Member',
          screen_access_type: 'default',
          send_invitation: true
        });
        setValidationErrors({});
        
        // Show success details
        const { user, invitation_sent, configurations } = response.data;
        const successMessage = `
          ✅ User ${user.user_email} created
          ✅ UserSubscriptions record created
          ✅ Screen access configured (${configurations.screens_configured} screens)
          ${invitation_sent ? '✅ Invitation email sent' : ''}
        `;
        
        // Show detailed success modal
        toast(successMessage, {
          duration: 8000,
          style: {
            maxWidth: '500px',
            whiteSpace: 'pre-line'
          }
        });
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create user';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFixUser = async (userId: string) => {
    setSaving(true);
    try {
      const response = await axiosInstance.post(`/admin/users/${userId}/fix-configuration`);
      
      if (response.data.success) {
        toast.success('User configuration fixed successfully!');
        setShowFixModal(false);
        setSelectedUser(null);
        fetchAllUsers(); // Refresh the list
        
        // Show what was fixed
        const { fixes_applied } = response.data;
        const fixMessage = fixes_applied.map((fix: string) => `✅ ${fix}`).join('\n');
        toast(fixMessage, {
          duration: 6000,
          style: {
            maxWidth: '400px',
            whiteSpace: 'pre-line'
          }
        });
      }
    } catch (error: any) {
      console.error('Error fixing user:', error);
      toast.error(error.response?.data?.error || 'Failed to fix user configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUserRole = async (userId: string) => {
    setSaving(true);
    try {
      const response = await axiosInstance.put(`/admin/users/${userId}/update-role`, {
        role_id: roleUpdateData.role_id,
        subscription_package: roleUpdateData.subscription_package,
        subscription_role: roleUpdateData.subscription_role
      });
      
      if (response.data.success) {
        toast.success('User role and package updated successfully!');
        setShowUpdateRoleModal(false);
        setSelectedUser(null);
        fetchAllUsers(); // Refresh the list
        
        // Show what was updated
        const selectedRole = userRoles.find(r => r.id === roleUpdateData.role_id);
        toast(`✅ Updated to ${selectedRole?.role_name || 'Unknown'} with ${roleUpdateData.subscription_package} package`, {
          duration: 6000
        });
      }
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast.error(error.response?.data?.error || 'Failed to update user role');
    } finally {
      setSaving(false);
    }
  };

  const getPackageRecommendation = (userType: string): string => {
    switch (userType) {
      case 'internal':
        return 'InternalEmployee';
      case 'broker':
        return 'EnterpriseBroker';
      case 'admin':
        return 'InternalEmployee';
      case 'agent':
      default:
        return 'ProAgentMonthly';
    }
  };

  const getRoleRecommendation = (userType: string): string => {
    switch (userType) {
      case 'internal':
      case 'admin':
        return 'Manager';
      case 'broker':
        return 'Admin';
      case 'agent':
      default:
        return 'Member';
    }
  };

  // Update recommendations when user type changes
  useEffect(() => {
    if (formData.user_type) {
      const recommendedPackage = getPackageRecommendation(formData.user_type);
      const recommendedRole = getRoleRecommendation(formData.user_type);
      
      setFormData(prev => ({
        ...prev,
        subscription_package: packages.find(p => p.subscription_package_name === recommendedPackage)?.subscription_package_name || prev.subscription_package,
        subscription_role: recommendedRole
      }));
    }
  }, [formData.user_type, packages]);

  // Load users when switching to fix tab
  useEffect(() => {
    if (activeTab === 'fix') {
      fetchAllUsers();
    }
  }, [activeTab]);

  // Re-fetch data if modal opens and data is empty
  useEffect(() => {
    if (showUpdateRoleModal && (userRoles.length === 0 || packages.length === 0)) {
      console.log('Modal opened but data is empty, fetching...');
      fetchInitialData();
    }
  }, [showUpdateRoleModal]);

  // Populate role update data when user is selected
  useEffect(() => {
    if (selectedUser && showUpdateRoleModal && userRoles.length > 0) {
      // Try to find the user's current role based on their user_type or subscription
      let currentRoleId = '';
      
      // Map common patterns to roles
      if (selectedUser.user_type === 'Admin' || selectedUser.subscription_package === 'InternalEmployee') {
        const adminRole = userRoles.find(r => r.role_name === 'ADMIN');
        currentRoleId = adminRole?.id || '';
      } else if (selectedUser.user_type === 'Broker' || selectedUser.subscription_package?.includes('Broker')) {
        const brokerRole = userRoles.find(r => r.role_name === 'BROKER');
        currentRoleId = brokerRole?.id || '';
      } else if (selectedUser.user_type === 'Agent' || selectedUser.subscription_package?.includes('Agent')) {
        const agentRole = userRoles.find(r => r.role_name === 'REAL_ESTATE_AGENT');
        currentRoleId = agentRole?.id || '';
      }

      setRoleUpdateData({
        role_id: currentRoleId,
        subscription_package: selectedUser.subscription_package || '',
        subscription_role: selectedUser.user_type === 'Admin' || selectedUser.user_type === 'Broker' ? 'Admin' : 'Member'
      });
    }
  }, [selectedUser, showUpdateRoleModal, userRoles]);

  if (loading && packages.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading setup data...</p>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">
              <FaUserPlus className="me-2" />
              Complete User Setup
            </h4>
            <small>Create users with all required configurations in one step</small>
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={() => setActiveTab(activeTab === 'create' ? 'fix' : 'create')}
          >
            {activeTab === 'create' ? 'Fix Existing Users' : 'Create New User'}
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        {activeTab === 'create' ? (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <FaUsers className="me-2" />
                      Basic Information
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.user_first_name}
                            onChange={(e) => setFormData({ ...formData, user_first_name: e.target.value })}
                            isInvalid={!!validationErrors.user_first_name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.user_first_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.user_last_name}
                            onChange={(e) => setFormData({ ...formData, user_last_name: e.target.value })}
                            isInvalid={!!validationErrors.user_last_name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.user_last_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.user_email}
                        onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                        isInvalid={!!validationErrors.user_email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.user_email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Phone (Optional)</Form.Label>
                      <Form.Control
                        type="tel"
                        value={formData.user_phone || ''}
                        onChange={(e) => setFormData({ ...formData, user_phone: e.target.value })}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>User Type <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={formData.user_type}
                        onChange={(e) => setFormData({ ...formData, user_type: e.target.value as any })}
                      >
                        <option value="agent">Agent</option>
                        <option value="broker">Broker</option>
                        <option value="internal">Internal Employee</option>
                        <option value="admin">Administrator</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        This determines default access levels and subscription recommendations
                      </Form.Text>
                    </Form.Group>

                    {formData.user_type === 'agent' && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Brokerage <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            value={formData.brokerage_id || ''}
                            onChange={(e) => setFormData({ ...formData, brokerage_id: e.target.value })}
                            isInvalid={!!validationErrors.brokerage_id}
                          >
                            <option value="">Select brokerage...</option>
                            {brokerages.map((brokerage) => (
                              <option key={brokerage.id} value={brokerage.id}>
                                {brokerage.brokerage_name}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.brokerage_id}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Agent License (Optional)</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.agent_license || ''}
                            onChange={(e) => setFormData({ ...formData, agent_license: e.target.value })}
                            placeholder="e.g., DRE #01234567"
                          />
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <FaCreditCard className="me-2" />
                      Subscription Setup (Required)
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info" className="mb-3">
                      <FaInfoCircle className="me-2" />
                      Every user MUST have a subscription record. This prevents navigation errors.
                    </Alert>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Subscription Package <span className="text-danger">*</span>
                        {formData.user_type && (
                          <Badge bg="success" className="ms-2">
                            Recommended: {getPackageRecommendation(formData.user_type)}
                          </Badge>
                        )}
                      </Form.Label>
                      <Form.Select
                        value={formData.subscription_package}
                        onChange={(e) => setFormData({ ...formData, subscription_package: e.target.value })}
                        isInvalid={!!validationErrors.subscription_package}
                      >
                        <option value="">Select package...</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.subscription_package_name}>
                            {pkg.subscription_package_name} - ${pkg.monthly_price}/mo
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.subscription_package}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Subscription Role
                        {formData.user_type && (
                          <Badge bg="success" className="ms-2">
                            Recommended: {getRoleRecommendation(formData.user_type)}
                          </Badge>
                        )}
                      </Form.Label>
                      <Form.Select
                        value={formData.subscription_role}
                        onChange={(e) => setFormData({ ...formData, subscription_role: e.target.value })}
                      >
                        <option value="Member">Member</option>
                        <option value="Admin">Admin</option>
                        <option value="Manager">Manager</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Controls access level within the subscription
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <FaShieldAlt className="me-2" />
                      Screen Access Control
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Access Configuration</Form.Label>
                      <Form.Check
                        type="radio"
                        id="access-default"
                        label="Use default access for subscription package"
                        checked={formData.screen_access_type === 'default'}
                        onChange={() => setFormData({ ...formData, screen_access_type: 'default' })}
                      />
                      <Form.Check
                        type="radio"
                        id="access-custom"
                        label="Configure custom screen access"
                        checked={formData.screen_access_type === 'custom'}
                        onChange={() => setFormData({ ...formData, screen_access_type: 'custom' })}
                      />
                    </Form.Group>

                    {formData.screen_access_type === 'custom' && (
                      <Alert variant="warning">
                        <FaExclamationTriangle className="me-2" />
                        Custom screen configuration will be available after user creation
                      </Alert>
                    )}

                    {formData.user_type === 'agent' && (
                      <Alert variant="info">
                        <FaInfoCircle className="me-2" />
                        <strong>Agent Restrictions:</strong> Agents won't have access to Broker Intelligence Hub, 
                        Company Profile, or administrative features by default.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <FaEnvelope className="me-2" />
                      Invitation
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="send-invitation"
                      label="Send invitation email to user"
                      checked={formData.send_invitation}
                      onChange={(e) => setFormData({ ...formData, send_invitation: e.target.checked })}
                    />
                    <Form.Text className="text-muted">
                      User will receive an email with a link to set their password
                    </Form.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-4">
              <Button
                type="submit"
                size="lg"
                disabled={saving}
                className="px-5"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Creating User...
                  </>
                ) : (
                  <>
                    <FaUserPlus className="me-2" />
                    Create User with Complete Setup
                  </>
                )}
              </Button>
            </div>
          </Form>
        ) : (
          // Fix Existing Users Tab
          <div>
            <Row className="mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  label="Show only users with issues"
                  checked={showOnlyIssues}
                  onChange={(e) => setShowOnlyIssues(e.target.checked)}
                />
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading users...</p>
              </div>
            ) : (
              <div>
                <Alert variant="info" className="mb-3">
                  <strong>Total Users: {allUsers.length}</strong> | 
                  <strong className="text-danger ms-2">With Issues: {allUsers.filter(u => u.has_issues).length}</strong>
                </Alert>
                
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Package</th>
                      <th>Status</th>
                      <th>Issues</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers
                      .filter(user => {
                        // Filter by search term
                        const matchesSearch = !searchTerm || 
                          `${user.user_first_name} ${user.user_last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.user_email.toLowerCase().includes(searchTerm.toLowerCase());
                        
                        // Filter by issues
                        const matchesIssueFilter = !showOnlyIssues || user.has_issues;
                        
                        return matchesSearch && matchesIssueFilter;
                      })
                      .map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.user_first_name} {user.user_last_name}</strong>
                      </td>
                      <td>{user.user_email}</td>
                      <td>
                        <Badge bg={user.user_type === 'internal' ? 'primary' : user.user_type === 'broker' ? 'info' : 'secondary'}>
                          {user.user_type || 'Agent'}
                        </Badge>
                      </td>
                      <td>{user.subscription_package || 'None'}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Badge bg={user.has_subscription ? 'success' : 'danger'}>
                            {user.has_subscription ? 'Active' : 'No Sub'}
                          </Badge>
                          <Badge bg={user.has_screen_access ? 'success' : 'warning'}>
                            {user.has_screen_access ? 'Access' : 'Limited'}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        {user.has_issues ? (
                          user.issues.map((issue, idx) => (
                            <div key={idx} className="text-danger small">
                              <FaTimesCircle className="me-1" />
                              {issue}
                            </div>
                          ))
                        ) : (
                          <span className="text-success">
                            <FaCheckCircle className="me-1" />
                            No issues
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowFixModal(true);
                            }}
                          >
                            <FaEdit className="me-2" />
                            Fix Configuration
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowUpdateRoleModal(true);
                            }}
                          >
                            <FaKey className="me-2" />
                            Update Role/Package
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Card.Body>

      {/* Fix User Modal */}
      <Modal show={showFixModal} onHide={() => setShowFixModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Fix User Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <h5>{selectedUser.user_first_name} {selectedUser.user_last_name}</h5>
              <p className="text-muted">{selectedUser.user_email}</p>
              
              <Alert variant="info">
                <strong>Issues to be fixed:</strong>
                <ul className="mb-0 mt-2">
                  {selectedUser.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              </Alert>

              <Alert variant="success">
                <strong>Actions that will be taken:</strong>
                <ul className="mb-0 mt-2">
                  {!selectedUser.has_subscription && (
                    <li>Create UserSubscriptions record with ProAgentMonthly package</li>
                  )}
                  {!selectedUser.has_screen_access && (
                    <li>Configure default screen access based on user type</li>
                  )}
                  <li>Ensure all navigation dependencies are met</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFixModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => selectedUser && handleFixUser(selectedUser.id)}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Fixing...
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                Fix Configuration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update User Role Modal */}
      <Modal show={showUpdateRoleModal} onHide={() => setShowUpdateRoleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Update User Role & Package
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              {console.log('Modal open - userRoles:', userRoles.length, 'packages:', packages.length)}
              {(userRoles.length === 0 || packages.length === 0) ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading roles and packages...</p>
                  <p className="text-muted small">Roles: {userRoles.length}, Packages: {packages.length}</p>
                </div>
              ) : (
                <>
              <h5>{selectedUser.user_first_name} {selectedUser.user_last_name}</h5>
              <p className="text-muted">{selectedUser.user_email}</p>
              
              <Alert variant="info">
                <strong>Current Configuration:</strong>
                <ul className="mb-0 mt-2">
                  <li>User Type: {selectedUser.user_type || 'Not set'}</li>
                  <li>Package: {selectedUser.subscription_package || 'Not set'}</li>
                  <li>Status: {selectedUser.subscription_status || 'Not set'}</li>
                </ul>
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>User Role</Form.Label>
                  <Form.Select
                    value={roleUpdateData.role_id}
                    onChange={(e) => {
                      const selectedRoleId = e.target.value;
                      const selectedRole = userRoles.find(r => r.id === selectedRoleId);
                      
                      // Auto-suggest package based on role
                      let suggestedPackage = roleUpdateData.subscription_package;
                      if (selectedRole) {
                        if (selectedRole.role_name === 'ADMIN') {
                          suggestedPackage = 'InternalEmployee';
                        } else if (selectedRole.role_name === 'BROKER') {
                          suggestedPackage = 'BrokerPlan';
                        } else if (selectedRole.role_name === 'REAL_ESTATE_AGENT') {
                          suggestedPackage = 'Agent CRM';
                        } else if (selectedRole.role_name === 'TEAM_LEAD') {
                          suggestedPackage = 'Professional';
                        }
                      }
                      
                      setRoleUpdateData({ 
                        ...roleUpdateData, 
                        role_id: selectedRoleId,
                        subscription_package: suggestedPackage
                      });
                    }}
                  >
                    <option value="">Select role...</option>
                    {userRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.role_name.replace(/_/g, ' ')} - {role.role_description}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select ADMIN role for KairosIQ team members to access all features including Investor Deals
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subscription Package</Form.Label>
                  <Form.Select
                    value={roleUpdateData.subscription_package}
                    onChange={(e) => setRoleUpdateData({ ...roleUpdateData, subscription_package: e.target.value })}
                  >
                    <option value="">Select package...</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.subscription_package_name}>
                        {pkg.subscription_package_name} - ${pkg.monthly_price}/mo
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subscription Role</Form.Label>
                  <Form.Select
                    value={roleUpdateData.subscription_role}
                    onChange={(e) => setRoleUpdateData({ ...roleUpdateData, subscription_role: e.target.value })}
                  >
                    <option value="">Select subscription role...</option>
                    <option value="Member">Member</option>
                    <option value="Admin">Admin</option>
                    <option value="Manager">Manager</option>
                  </Form.Select>
                </Form.Group>
              </Form>

              {/* Show what will change */}
              {roleUpdateData.role_id && roleUpdateData.subscription_package && (
                <Alert variant="success" className="mt-3">
                  <FaCheckCircle className="me-2" />
                  <strong>Changes to be applied:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Role: {userRoles.find(r => r.id === roleUpdateData.role_id)?.role_name.replace(/_/g, ' ')}</li>
                    <li>Package: {roleUpdateData.subscription_package}</li>
                    <li>Access Level: {roleUpdateData.subscription_role}</li>
                  </ul>
                </Alert>
              )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateRoleModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => selectedUser && handleUpdateUserRole(selectedUser.id)}
            disabled={saving || !roleUpdateData.role_id || !roleUpdateData.subscription_package}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FaKey className="me-2" />
                Update Role & Package
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default CompleteUserSetup;